## `components/SchoolPath.vue`
<template>
    <div class="school-names">
        <span class="school-name" :class="{ set: startSet }">
            <i class="fa-solid fa-school" />
            <span>{{ start }}</span>
                <slot name="start-action"></slot>
        </span>
        <i class="fa-solid fa-arrow-right" />
        <span class="school-name" :class="{ set: destSet }">
            <i class="fa-solid fa-school" />
            <span>{{ dest }}</span>
        </span>
    </div>
</template>
<script setup lang="ts">
withDefaults(defineProps<{ start: string; dest: string; startSet?: boolean; destSet?: boolean }>(), {
    startSet: false,
    destSet: false,
});
</script>
<style>
/* Enhanced Search Styles */
.enhanced-search {
    margin: 2rem 0;
    position: relative;
}

.enhanced-search::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #667eea, #764ba2, #667eea);
    border-radius: 15px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.enhanced-search:focus-within::before {
    opacity: 1;
}

/* Selected Items Styles */
.selected-program,
.selected-minor {
    margin: 2rem 0;
}

.program-card,
.minor-card {
    background: linear-gradient(135deg, #f8f9ff, #e8f2ff);
    border: 2px solid rgba(102, 126, 234, 0.2);
    border-radius: 15px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
}

.program-card:hover,
.minor-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
}

.program-icon,
.minor-icon {
    width: 60px;
    height: 60px;
    border-radius: 15px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.8rem;
}

.program-details,
.minor-details {
    flex: 1;
    text-align: left;
}

.program-details h4,
.minor-details h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    color: #333;
    font-weight: 600;
}

.college-name {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
}

.minor-notice {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: #f39c12;
    font-weight: 500;
    font-size: 0.9rem;
}

.check-icon {
    color: #4CAF50;
    font-size: 2rem;
}

/* Selected Courses Grid */
.selected-courses {
    margin: 2rem 0;
    text-align: left;
}

.selected-courses h4 {
    color: #333;
    margin-bottom: 1rem;
    font-size: 1.2rem;
}

.courses-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

.course-card {
    background: white;
    border: 1px solid #e1e8ed;
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.3s ease;
}

.course-card:hover {
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.1);
}

.course-info {
    flex: 1;
}

.course-name {
    font-size: 0.95rem;
    color: #333;
    line-height: 1.4;
}

.remove-btn {
    background: #ff4757;
    color: white;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.remove-btn:hover {
    background: #ff3742;
    transform: scale(1.1);
}

/* Enhanced Loading Styles */
.enhanced-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 500px;
    padding: 3rem;
}

.loading-content {
    text-align: center;
    max-width: 600px;
    width: 100%;
}

.loading-header {
    margin-bottom: 3rem;
}

.loading-header h3 {
    font-size: 1.8rem;
    color: #333;
    margin-bottom: 1rem;
    font-weight: 600;
}

.loading-animation {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin: 1rem 0;
}

.dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #667eea;
    animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

.enhanced-error-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 600px;
    padding: 2rem;
    background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 50%, #fbb6ce 100%);
    border-radius: 25px;
    margin: 2rem auto;
    max-width: 900px;
    box-shadow: 0 20px 60px rgba(239, 68, 68, 0.1);
    backdrop-filter: blur(10px);
}

.error-content {
    text-align: center;
    max-width: 700px;
    width: 100%;
    animation: slideUp 0.6s ease-out;
}

/* Error Visual Section */
.error-visual {
    margin-bottom: 3rem;
}

.error-icon-wrapper {
    position: relative;
    display: inline-block;
    margin-bottom: 2rem;
}

.error-icon {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 2.5rem;
    position: relative;
    z-index: 2;
    animation: pulse 2s infinite;
    box-shadow: 0 10px 30px rgba(239, 68, 68, 0.3);
}

.error-ripple {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid #ef4444;
    border-radius: 50%;
    opacity: 0;
    transform: scale(1);
    animation: ripple 2s infinite;
}

.error-header h3 {
    font-size: 2.2rem;
    color: #991b1b;
    margin-bottom: 0.5rem;
    font-weight: 700;
    line-height: 1.2;
}

.error-subtitle {
    font-size: 1.1rem;
    color: #7f1d1d;
    opacity: 0.8;
    margin: 0;
    font-weight: 400;
}

/* Error Message Card */
.error-message {
    margin: 2rem 0;
}

.message-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 15px;
    padding: 2rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    text-align: left;
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.1);
    backdrop-filter: blur(10px);
}

.message-icon {
    color: #ef4444;
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
}

.message-card p {
    margin: 0;
    font-size: 1.1rem;
    color: #374151;
    line-height: 1.6;
}

/* Error Details Section */
.error-details-section {
    margin: 3rem 0;
}

.details-toggle {
    background: rgba(255, 255, 255, 0.8);
    border: 2px solid rgba(239, 68, 68, 0.3);
    border-radius: 50px;
    padding: 1rem 2rem;
    font-size: 1rem;
    font-weight: 500;
    color: #991b1b;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    backdrop-filter: blur(10px);
}

.details-toggle:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: #ef4444;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(239, 68, 68, 0.2);
}

.details-toggle i {
    font-size: 1.2rem;
    transition: transform 0.3s ease;
}

/* Details Content */
.details-content {
    margin-top: 2rem;
    text-align: left;
}

.solutions-header {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 2rem;
    justify-content: center;
}

.solutions-header i {
    color: #f59e0b;
    font-size: 1.5rem;
}

.solutions-header h4 {
    margin: 0;
    font-size: 1.3rem;
    color: #991b1b;
    font-weight: 600;
}

.solutions-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;
    max-width: 600px;
    margin: 0 auto;
}

.solution-card {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(249, 115, 22, 0.2);
    border-radius: 15px;
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    transition: all 0.3s ease;
    animation: slideInUp 0.5s ease-out both;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.1);
}

.solution-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(249, 115, 22, 0.15);
    border-color: #f97316;
}

.solution-number {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f59e0b, #f97316);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1rem;
    flex-shrink: 0;
    box-shadow: 0 4px 15px rgba(249, 115, 22, 0.3);
}

.solution-content {
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 0.8rem;
}

.solution-icon {
    color: #f59e0b;
    font-size: 1.2rem;
    margin-top: 0.2rem;
    flex-shrink: 0;
}

.solution-content p {
    margin: 0;
    color: #374151;
    line-height: 1.5;
    font-size: 1rem;
}

/* Error Actions */
.error-actions {
    margin-top: 3rem;
}

.actions-group {
    display: flex;
    gap: 1.5rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;
}
.school-names {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  background: #e5e7eb;
  border-radius: 12px;
  padding: 12px 20px;
  gap: 12px;
  min-height: 50px;
}

.school-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex: 0 0 auto;
  color: #9ca3af;
  font-weight: 500;
}

.school-name.set {
  color: #001E62;
}

.school-name i {
  font-size: 1rem;
}

.school-names .fa-arrow-right {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  color: #9ca3af;
}




</style>